/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { AUDIO_VISUALIZATION_DIMENSIONS } from '../constants';

const {
  DATA_POINT_WIDTH,
  DATA_POINT_HEIGHT,
  DATA_POINT_MARGIN,
  DATA_POINT_X_OFFSET,
  DATA_POINT_Y_OFFSET
} = AUDIO_VISUALIZATION_DIMENSIONS;

interface AudioWaveProps {
  audioRef: MutableRefObject<HTMLAudioElement>;
  audioWaveContainerRef: MutableRefObject<HTMLDivElement>;
  data: Array<number>;
  duration: number;
}

const AudioWave = (props: AudioWaveProps) => {
  const { audioRef, audioWaveContainerRef, data, duration } = props;
  const [startingTimestamp, setStartingTimestamp] = useState(0);
  const [isPausedStart, setIsPausedStart] = useState(false);
  const audioWaveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioWaveCanvasCtx = useRef<CanvasRenderingContext2D | null>(null);

  var animationFrameId = useRef(null);

  const initializeCanvasDimensions = useCallback(() => {
    const canvasCtx = audioWaveCanvasRef.current;

    canvasCtx.width = 400;
    canvasCtx.height = 100;
  }, []);

  useEffect(() => {
    // Initialize
    if (audioWaveCanvasRef.current) {
      audioWaveCanvasCtx.current = audioWaveCanvasRef.current.getContext('2d');
      initializeCanvasDimensions();
    }
  }, [initializeCanvasDimensions]);

  const drawEmptyDataPoints = useCallback(() => {
    if (data) {
      data.forEach((dataPoint, i) => {
        if (audioWaveCanvasCtx.current) {
          audioWaveCanvasCtx.current.beginPath();

          audioWaveCanvasCtx.current.fillStyle = '#8D8D8D';
          audioWaveCanvasCtx.current.fillRect(
            i * DATA_POINT_MARGIN + DATA_POINT_X_OFFSET, // x position
            DATA_POINT_Y_OFFSET, // y position
            DATA_POINT_WIDTH, // rect width
            DATA_POINT_HEIGHT * dataPoint // rect height
          );
          audioWaveCanvasCtx.current.fillRect(
            i * DATA_POINT_MARGIN + DATA_POINT_X_OFFSET, // x position
            DATA_POINT_Y_OFFSET, // y position
            DATA_POINT_WIDTH, // rect width
            -DATA_POINT_HEIGHT * dataPoint // rect height
          );
          audioWaveCanvasCtx.current.stroke();
          audioWaveCanvasCtx.current.closePath();
        }
      });
    }
  }, [data]);

  const resetCanvasForNewFrame = () => {
    const audioWaveCanvas = audioWaveCanvasRef.current;
    if (audioWaveCanvas) {
      audioWaveCanvasCtx.current.clearRect(
        0,
        0,
        audioWaveCanvas.width,
        audioWaveCanvas.height
      );
    }
  };

  const drawCanvasBackground = () => {
    if (audioWaveCanvasCtx.current && audioWaveCanvasRef.current) {
      audioWaveCanvasCtx.current.fillStyle = 'rgba(255, 255, 255, 0)';
      audioWaveCanvasCtx.current.fillRect(
        0,
        0,
        audioWaveCanvasRef.current.width,
        audioWaveCanvasRef.current.height
      );
    }
  };

  const drawInitialAudioWave = useCallback(() => {
    resetCanvasForNewFrame();
    drawCanvasBackground();
    drawEmptyDataPoints();
  }, [drawEmptyDataPoints]);

  const drawAudioDataPoints = useCallback(() => {
    if (!audioRef.current) {
      return;
    }
    if (!audioRef.current.paused) {
      // time calculations
      const timeElapsed = audioRef.current.currentTime;
      const audioProgressPercent = timeElapsed / duration;
      // draw the audio lines
      const numberOfDataPoints = data.length;
      const highlightIndex = numberOfDataPoints * audioProgressPercent;

      const wholeHighlightIndex = Math.floor(highlightIndex);
      let decimalOpacityIndex = highlightIndex - wholeHighlightIndex;

      if (decimalOpacityIndex > 1) {
        decimalOpacityIndex = 1;
      }

      if (decimalOpacityIndex < 0.5) {
        decimalOpacityIndex = 0.5;
      }

      data.forEach((dataPoint, i) => {
        if (audioWaveCanvasCtx.current) {
          audioWaveCanvasCtx.current.beginPath();

          let fillColor = '#0F62FE';
          if (i > wholeHighlightIndex) {
            fillColor = '#8D8D8D';
          }

          if (i - 1 === wholeHighlightIndex) {
            fillColor = `#8D8D8D`;
          }

          audioWaveCanvasCtx.current.fillStyle = fillColor;
          audioWaveCanvasCtx.current.fillRect(
            i * DATA_POINT_MARGIN + DATA_POINT_X_OFFSET, // x position
            DATA_POINT_Y_OFFSET, // y position
            DATA_POINT_WIDTH, // rect width
            DATA_POINT_HEIGHT * dataPoint // rect height
          );
          audioWaveCanvasCtx.current.fillRect(
            i * DATA_POINT_MARGIN + DATA_POINT_X_OFFSET, // x position
            DATA_POINT_Y_OFFSET, // y position
            DATA_POINT_WIDTH, // rect width
            -DATA_POINT_HEIGHT * dataPoint // rect height
          );
          audioWaveCanvasCtx.current.stroke();
          audioWaveCanvasCtx.current.closePath();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioRef, data, duration, isPausedStart, startingTimestamp]);

  const draw = useCallback(() => {
    if (!audioRef.current) {
      return;
    }
    if (!audioRef.current.paused) {
      resetCanvasForNewFrame();
      drawCanvasBackground();
      drawAudioDataPoints();
      animationFrameId.current = requestAnimationFrame(draw);
    }
  }, [audioRef, drawAudioDataPoints]);

  useEffect(() => {
    drawInitialAudioWave();
  }, [data, drawInitialAudioWave]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    if (!audioRef.current.paused && startingTimestamp === 0) {
      setStartingTimestamp(Date.now());
      setIsPausedStart(false);
      return;
    }
    if (
      audioRef.current.paused &&
      audioRef.current.ended &&
      startingTimestamp !== 0
    ) {
      // stop drawing
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      setStartingTimestamp(0);
    }
    if (audioRef.current.paused && !isPausedStart) {
      setIsPausedStart(true);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    }
    if (!audioRef.current.paused && isPausedStart) {
      setIsPausedStart(false);
    }
  }, [audioRef, audioRef.current.paused, isPausedStart, startingTimestamp]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    // stop drawing
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    setStartingTimestamp(0);
  }, [audioRef, audioRef.current.src]);

  useEffect(() => {
    if (startingTimestamp !== 0) {
      draw();
    }
  }, [draw, startingTimestamp]);

  return (
    <div ref={audioWaveContainerRef} className="audiowave">
      <canvas ref={audioWaveCanvasRef} />
    </div>
  );
};

export { AudioWave };
